import React, { type ReactElement, type ReactNode } from "react";
import { Button, Card, cn } from "@ndd/ui";
import type {
  PatientPageAppointment,
  PatientPageHeader as PatientPageHeaderFacts,
} from "../../../../shared/api-types";
import {
  THEMED_CARD,
  THEMED_CARD_TITLE,
  THEMED_DIVIDE,
  THEMED_QUIET_LINK,
} from "../../lib/always-look";
import {
  appointmentDateLabel,
  appointmentKindLabel,
  patientHeaderFacts,
} from "../../lib/patient-page-client";
import { patientName } from "../../lib/patients-client";
import { PriorSoapNoteMarkdown } from "../appointments/prior-soap-note-markdown";
import { Eyebrow } from "./eyebrow";
import { PatientAvatar } from "./patient-list-parts";

// The patient page (NO-849): the header, About them and Appointments, laid out
// in two columns with the right one holding Quick facts and Goals (NO-852) and
// the Notifications card (NO-851). A
// leaf module (`import React`, relative imports, no router, no queries) so
// `tsx --test` renders it. The page supplies the back link, each row's Note
// link and About them's body (the read-only note or the editor); every missing
// value is left out and every state is neutral text, never an alarm.

export type RenderBackLink = (props: {
  className: string;
  children: ReactNode;
}) => ReactElement;

export type RenderNoteLink = (props: {
  appointmentId: string;
  className: string;
  ariaLabel: string;
  children: ReactNode;
}) => ReactElement;

export const PATIENT_PAGE_UNAVAILABLE_MESSAGE =
  "This Client's page isn't available yet. Ask an admin to link your Healthie account.";

export const PATIENT_NOT_IN_LIST_MESSAGE = "This Client isn't in your patients list.";

export const ABOUT_THEM_EMPTY_MESSAGE = "Nothing noted yet.";

export const NO_APPOINTMENTS_MESSAGE = "No appointments yet.";

const BACK_LINK_LABEL = "← Patients";

const MESSAGES_LINK_LABEL = "Messages";

export type RenderMessagesLink = (props: {
  className: string;
  children: ReactNode;
}) => ReactElement;

/** A card's heading; the Notifications card shares it. */
export const CARD_HEADING = cn(
  "font-sans text-body-lg font-semibold text-foreground",
  THEMED_CARD_TITLE
);

/** "← Patients" and each row's Note: quiet underlined links. */
const PAGE_LINK = cn(
  "shrink-0 text-body font-semibold text-foreground underline underline-offset-4",
  THEMED_QUIET_LINK
);

/** The name row and the facts under it. */
export function PatientPageHeader({
  patientId,
  header,
  timezone,
  now,
  renderBackLink,
  renderMessagesLink,
}: {
  patientId: string;
  header: PatientPageHeaderFacts;
  timezone: string;
  now: Date;
  renderBackLink: RenderBackLink;
  /** NO-925: the Messages link, present only with patient-detail and patient-messaging on. */
  renderMessagesLink?: RenderMessagesLink;
}): ReactElement {
  const facts = patientHeaderFacts(header, timezone, now);
  const avatarPatient = {
    id: patientId,
    displayName: header.name ?? null,
    initials: header.initials ?? null,
  };

  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <PatientAvatar patient={avatarPatient} size="header" />
        <h1
          data-patient-name
          className="min-w-0 flex-1 truncate font-sans text-3xl font-bold tracking-tight text-foreground brand:text-display brand:leading-[1.15]"
        >
          {patientName(avatarPatient)}
        </h1>
        {renderMessagesLink
          ? renderMessagesLink({ className: PAGE_LINK, children: MESSAGES_LINK_LABEL })
          : null}
        {renderBackLink({ className: PAGE_LINK, children: BACK_LINK_LABEL })}
      </div>
      {facts.length > 0 ? <PatientFacts facts={facts} /> : null}
    </header>
  );
}

function PatientFacts({ facts }: { facts: string[] }): ReactElement {
  return (
    <ul
      aria-label="Client details"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body text-muted-foreground"
    >
      {facts.map((fact, index) => (
        <li key={fact} className="flex items-center gap-3">
          {index > 0 ? (
            <span aria-hidden="true" className="text-subtle">
              •
            </span>
          ) : null}
          {fact}
        </li>
      ))}
    </ul>
  );
}

/** A state with no Client facts: a plain line and the way back to Patients. */
export function PatientPageMessage({
  renderBackLink,
  children,
}: {
  renderBackLink: RenderBackLink;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {renderBackLink({ className: PAGE_LINK, children: BACK_LINK_LABEL })}
      </div>
      {typeof children === "string" ? (
        <p className="text-body text-muted-foreground">{children}</p>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * The left column's cards and the right column. With no `aside` the right
 * column stays empty and hidden below lg; the page passes NO-852's Quick facts
 * and Goals and NO-851's Notifications card as `aside`, which shows at every
 * width, stacked under the left column below lg.
 */
export function PatientPageColumns({
  aside,
  children,
}: {
  aside?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-6">{children}</div>
      {aside === undefined ? (
        <div data-patient-page-aside="" className="hidden lg:block" />
      ) : (
        <div data-patient-page-aside="" className="flex min-w-0 flex-col gap-6">
          {aside}
        </div>
      )}
    </div>
  );
}

/** About them: the Patient Quick Note, when it was last updated, and Edit. */
export function AboutThemCard({
  updatedLabel,
  onEdit,
  children,
}: {
  updatedLabel: string | null;
  onEdit?: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <Card variant="hero" role="region" aria-label="About them" className="px-7 py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className={CARD_HEADING}>About them</h2>
        {updatedLabel ? <UpdatedLabel label={updatedLabel} /> : null}
      </div>
      <div className="mt-4">{children}</div>
      {onEdit ? (
        <Button
          type="button"
          variant="quiet-link"
          className="mt-4 h-auto px-0 py-0 text-body"
          onClick={onEdit}
        >
          Edit
        </Button>
      ) : null}
    </Card>
  );
}

function UpdatedLabel({ label }: { label: string }): ReactElement {
  return (
    <p data-about-them-updated="" className="flex items-center gap-2">
      {/* Decorative, under the always theme only; it carries no status. */}
      <span
        aria-hidden="true"
        className="hidden size-[7px] shrink-0 rounded-full bg-primary brand:inline-block"
      />
      <Eyebrow>{label}</Eyebrow>
    </p>
  );
}

/** About them's body for a note with nothing in it yet. */
export function AboutThemEmptyLine(): ReactElement {
  return <p className="text-body text-muted-foreground">{ABOUT_THEM_EMPTY_MESSAGE}</p>;
}

/** The Client's appointments in the order the server sent them (newest first). */
export function AppointmentsCard({
  appointments,
  timezone,
  now,
  renderNoteLink,
}: {
  appointments: PatientPageAppointment[];
  timezone: string;
  now: Date;
  renderNoteLink: RenderNoteLink;
}): ReactElement {
  return (
    <section
      aria-label="Appointments"
      className={cn("rounded-xl border border-border bg-card px-7 py-6", THEMED_CARD)}
    >
      <h2 className={CARD_HEADING}>Appointments</h2>
      {appointments.length === 0 ? (
        <p className="mt-4 text-body text-muted-foreground">
          {NO_APPOINTMENTS_MESSAGE}
        </p>
      ) : (
        <ul className={cn("mt-4 divide-y divide-border", THEMED_DIVIDE)}>
          {appointments.map((appointment) => (
            <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              dateLabel={appointmentDateLabel(appointment.startsAt, timezone, now)}
              renderNoteLink={renderNoteLink}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AppointmentRow({
  appointment,
  dateLabel,
  renderNoteLink,
}: {
  appointment: PatientPageAppointment;
  dateLabel: string;
  renderNoteLink: RenderNoteLink;
}): ReactElement {
  const { soap } = appointment;

  return (
    <li
      data-appointment-id={appointment.id}
      className="grid gap-x-6 gap-y-1 py-4 first:pt-0 last:pb-0 sm:grid-cols-[9rem_minmax(0,1fr)]"
    >
      <span className="text-body font-semibold text-foreground">{dateLabel}</span>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-body text-foreground">
            {appointmentKindLabel(appointment)}
          </span>
          {renderNoteLink({
            appointmentId: appointment.id,
            className: PAGE_LINK,
            ariaLabel: `Note, ${dateLabel}`,
            children: "Note",
          })}
        </div>
        {soap?.subjective ? (
          <SoapExcerpt label="Subjective" markdown={soap.subjective} />
        ) : null}
        {soap?.plan ? <SoapExcerpt label="Plan" markdown={soap.plan} /> : null}
      </div>
    </li>
  );
}

function SoapExcerpt({
  label,
  markdown,
}: {
  label: string;
  markdown: string;
}): ReactElement {
  return (
    <div data-soap-excerpt={label.toLowerCase()} className="mt-3">
      <Eyebrow as="p">{label}</Eyebrow>
      <div className="mt-1">
        <PriorSoapNoteMarkdown markdown={markdown} />
      </div>
    </div>
  );
}
